import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Chip, CircularProgress, IconButton, InputAdornment, Stack, SvgIcon,
  TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { ModulePermission } from '../../../../../app/services/api/generated';

interface RolePermissionsTabProps {
  personaDescription?: string;
  /** Exact permission names the persona has (Persona.personaRoles) - finer-grained than module-level access. */
  grantedPermissions: string[];
  modulePermissions: ModulePermission[];
  /** Keyed by lowercased permission name - shown as a tooltip on the permission pill when present. */
  permissionDescriptions?: Record<string, string>;
  /** Permission names the acting admin holds - a module only renders if they hold at least one of its permissions, so they can't see (or grant) systems/modules outside their own access. */
  adminPermissions: string[];
}

interface ModuleAccessSummary {
  key: string;
  module: ModulePermission;
  grantedCount: number;
  totalCount: number;
}

interface SystemAccessSummary {
  system: string;
  modules: ModuleAccessSummary[];
  grantedCount: number;
  totalCount: number;
}

// Fixed display order for the systems we know about; anything else sorts alphabetically after these.
const SYSTEM_ORDER = ['Orderly for...', 'Orderly for Stores', 'Orderly for Distributors'];

const systemSortIndex = (system: string): number => {
  const index = SYSTEM_ORDER.indexOf(system);
  return index === -1 ? SYSTEM_ORDER.length : index;
};

const RING_SIZE = 32;

// Two stacked determinate rings (grey track + coloured value) read better at small sizes than a linear bar.
const ProgressCircle = ({ percent, size = RING_SIZE }: { percent: number; size?: number }) => {
  const theme = useTheme();
  // Full and mid-range both use primary so they reflect the active branding colour; only the low tier keeps a distinct warning colour.
  const color = percent >= 50
    ? theme.palette.primary.main
    : theme.palette.warning.main;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={5}
        sx={{ color: theme.palette.custom.gray[200], position: 'absolute', left: 0, top: 0 }}
      />
      <CircularProgress
        variant="determinate"
        value={percent}
        size={size}
        thickness={5}
        sx={{ color, position: 'absolute', left: 0, top: 0, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }}
      />
    </Box>
  );
};

/** Read-only module access overview: system toggle buttons show total progress, panels underneath are per module. */
const RolePermissionsComponent = ({
  personaDescription, grantedPermissions, modulePermissions, permissionDescriptions = {}, adminPermissions,
}: RolePermissionsTabProps) => {
  const { t } = useTranslation('manageUsers');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSystem, setActiveSystem] = useState<string | null>(null);

  const grantedSet = useMemo(
    () => new Set(grantedPermissions.filter(Boolean).map(permission => permission.toLowerCase())),
    [grantedPermissions],
  );

  const adminPermissionSet = useMemo(
    () => new Set(adminPermissions.filter(Boolean).map(permission => permission.toLowerCase())),
    [adminPermissions],
  );

  // Only modules with at least one granted permission are shown at all, at any level - and only
  // if the acting admin themselves holds at least one permission in that module (fails closed: no
  // admin permissions in a module means it's invisible here, not "unrestricted").
  const moduleSummaries = useMemo<ModuleAccessSummary[]>(() => (
    modulePermissions
      .map((module) => {
        const permissions = (module.permissions ?? []).filter(Boolean);
        const grantedCount = permissions.filter(permission => grantedSet.has(permission.toLowerCase())).length;
        const adminHasAccess = permissions.some(permission => adminPermissionSet.has(permission.toLowerCase()));
        // Module names can repeat across systems, so the system is part of the identity, not just the module name.
        const key = `${module.system ?? ''}::${module.module ?? ''}`;
        return { key, module, grantedCount, totalCount: permissions.length, adminHasAccess };
      })
      .filter(summary => summary.grantedCount > 0 && summary.adminHasAccess)
  ), [modulePermissions, grantedSet, adminPermissionSet]);

  // Grouping by system also means a system with nothing granted (or nothing the admin can see) simply never gets an entry here.
  const systemSummaries = useMemo<SystemAccessSummary[]>(() => {
    const bySystem = new Map<string, ModuleAccessSummary[]>();

    moduleSummaries.forEach((summary) => {
      const systemName = summary.module.system ?? t('otherSystem', 'Other');
      const existing = bySystem.get(systemName) ?? [];
      existing.push(summary);
      bySystem.set(systemName, existing);
    });

    return Array.from(bySystem.entries())
      .map(([system, modules]) => {
        const sortedModules = [...modules].sort((a, b) => (a.module.module ?? '').localeCompare(b.module.module ?? ''));
        return {
          system,
          modules: sortedModules,
          grantedCount: sortedModules.reduce((sum, m) => sum + m.grantedCount, 0),
          totalCount: sortedModules.reduce((sum, m) => sum + m.totalCount, 0),
        };
      })
      .sort((a, b) => systemSortIndex(a.system) - systemSortIndex(b.system) || a.system.localeCompare(b.system));
  }, [moduleSummaries, t]);

  // Keeps the active tab valid as the persona/data changes, defaulting to the first system.
  useEffect(() => {
    if (systemSummaries.length === 0) {
      setActiveSystem(null);
      return;
    }
    setActiveSystem(prev => (prev && systemSummaries.some(summary => summary.system === prev) ? prev : systemSummaries[0].system));
  }, [systemSummaries]);

  const activeSummary = systemSummaries.find(summary => summary.system === activeSystem);

  // Narrows the active system's modules to the search term - a module whose name matches keeps all its granted pills, otherwise only matching pills remain.
  const renderedModules = useMemo(() => {
    if (!activeSummary) return [];

    const term = searchTerm.trim().toLowerCase();

    return activeSummary.modules
      .map((moduleSummary) => {
        const grantedPermissionsList = (moduleSummary.module.permissions ?? [])
          .filter(Boolean)
          .filter(permission => grantedSet.has(permission.toLowerCase()));

        if (!term) return { ...moduleSummary, displayedPermissions: grantedPermissionsList };

        const moduleNameMatches = (moduleSummary.module.module ?? '').toLowerCase().includes(term);
        const displayedPermissions = moduleNameMatches
          ? grantedPermissionsList
          : grantedPermissionsList.filter(permission =>
            permission.toLowerCase().includes(term)
            || (permissionDescriptions[permission.toLowerCase()] ?? '').toLowerCase().includes(term));

        return { ...moduleSummary, displayedPermissions };
      })
      .filter(moduleSummary => moduleSummary.displayedPermissions.length > 0);
  }, [activeSummary, searchTerm, grantedSet, permissionDescriptions]);

  const [expandedModule, setExpandedModule] = useState<string | false>(false);
  // Panels start open by default; the first click on any panel switches to "one at a time" mode.
  const [hasToggledModule, setHasToggledModule] = useState(false);

  // Resets back to "all open" whenever the visible set changes (system switch or new search).
  useEffect(() => {
    setHasToggledModule(false);
    setExpandedModule(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSystem, searchTerm]);

  const handleModuleToggle = (key: string) => (_: unknown, isExpanded: boolean) => {
    setHasToggledModule(true);
    setExpandedModule(isExpanded ? key : false);
  };

  return (
    <Box>
      {personaDescription && (
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ mb: 3 }}>
          {t('role')}: {personaDescription}
        </Typography>
      )}

      {systemSummaries.length === 0 ? null : (
        <>
          <Stack direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 3 }}>
            <ToggleButtonGroup
              value={activeSystem}
              exclusive
              onChange={(_, value) => value && setActiveSystem(value)}
              sx={{ flexWrap: 'wrap' }}
            >
              {systemSummaries.map((summary) => {
                const percent = summary.totalCount === 0 ? 0 : (summary.grantedCount / summary.totalCount) * 100;

                return (
                  <ToggleButton key={summary.system}
                    value={summary.system}
                    sx={{ gap: 2, px: 3, py: 2, minWidth: 220 }}>
                    <Stack direction="row"
                      alignItems="center"
                      gap={3}
                      sx={{ width: '100%' }}>
                      <Typography variant="textSM"
                        fontWeight={600}>
                        {summary.system}
                      </Typography>
                      <Stack direction="row"
                        alignItems="center"
                        gap={1}
                        sx={{ ml: 'auto' }}>
                        <ProgressCircle size={15}
                          percent={percent} />
                        <Typography variant="textXS"
                          color="text.secondary">
                          {summary.grantedCount}/{summary.totalCount}
                        </Typography>
                      </Stack>
                    </Stack>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>

            <TextField
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('searchPermissions', 'Search permissions')}
              size="small"
              sx={{ width: 280, maxWidth: '100%' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SvgIcon fontSize="small"><SearchIcon /></SvgIcon></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small"
                      aria-label={t('clearFilters')}
                      onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {renderedModules.length === 0 ? (
            <Typography variant="textSM"
              color="text.secondary">
              {t('noResults', 'No results')}
            </Typography>
          ) : (
            // Flex gap, not Stack's margin-based spacing, since the theme zeroes margin on expanded accordions.
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {renderedModules.map(moduleSummary => (
                <Accordion key={moduleSummary.key}
                  expanded={hasToggledModule ? expandedModule === moduleSummary.key : true}
                  onChange={handleModuleToggle(moduleSummary.key)}
                  disableGutters
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: 'custom.gray.50' }}>
                    <Stack direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ width: '100%', pr: 2 }}>
                      <Typography variant="textSM"
                        fontWeight={600}>
                        {moduleSummary.module.module}
                      </Typography>
                      <Typography variant="textXS"
                        color="text.secondary">
                        {moduleSummary.grantedCount} / {moduleSummary.totalCount}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction="row"
                      flexWrap="wrap"
                      gap={1}>
                      {moduleSummary.displayedPermissions.map((permission) => {
                        const description = permissionDescriptions[permission.toLowerCase()];
                        return (
                          <Chip key={permission}
                            label={permission}
                            title={description || undefined}
                            size="small"
                            color="primary"
                            variant="outlined" />
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default RolePermissionsComponent;

